/**
 * Market Feature Registry — single source of truth for per-market navigation & features.
 *
 * Feature IDs map 1:1 to route paths (without leading slash).
 * Navigation tabs, route guards, and homepage cards all derive from this config.
 */

/* ── Feature definitions (shared across markets) ── */
const FEATURES = {
  home:                  { id: 'home',                  path: '/overview',              tKey: 'markets',              icon: null },
  data_processing:       { id: 'data_processing',       path: '/data-processing',       tKey: 'dataProcessing',       icon: null },
  data_docs:             { id: 'data_docs',             path: '/data-docs',             tKey: 'dataDocs',             icon: null },
  news_intelligence:     { id: 'news_intelligence',     path: '/news-intelligence',     tKey: 'newsIntelligence',     icon: null },
  short_term_prediction: { id: 'short_term_prediction', path: '/short-term-prediction', tKey: 'shortTermPrediction',  icon: null },
  strategy_matching:     { id: 'strategy_matching',     path: '/strategy-matching',     tKey: 'strategyMatching',     icon: null },
  sagax_agent:           { id: 'sagax_agent',           path: '/sagax-agent',           tKey: 'sagaxAgent',           icon: null },
};

/* ── Per-market feature lists (order = nav tab order) ── */
const MARKET_FEATURES = {
  a_stock: [
    FEATURES.home,
    FEATURES.data_processing,
    FEATURES.data_docs,
    FEATURES.news_intelligence,
    FEATURES.short_term_prediction,
    FEATURES.strategy_matching,
    FEATURES.sagax_agent,
  ],
  chinese_futures: [
    FEATURES.home,
    FEATURES.data_processing,
    FEATURES.data_docs,
    FEATURES.news_intelligence,
    FEATURES.short_term_prediction,
    FEATURES.strategy_matching,
    FEATURES.sagax_agent,
  ],
  crypto: [
    FEATURES.home,
    FEATURES.data_processing,
    FEATURES.data_docs,
    FEATURES.news_intelligence,
    FEATURES.strategy_matching,
    FEATURES.sagax_agent,
  ],
};

/* ── Features available in ALL markets (never filtered out) ── */
const CROSS_MARKET_FEATURES = new Set([
  'news_intelligence',
  'sagax_agent',
  'data_docs',
]);

/**
 * Get ordered navigation tabs for a given market.
 * @param {string} marketId — 'a_stock' | 'chinese_futures' | 'crypto'
 * @returns {Array<{id, path, tKey}>}
 */
export function getNavTabsForMarket(marketId) {
  return MARKET_FEATURES[marketId] || MARKET_FEATURES.a_stock;
}

/**
 * Check whether a feature is available for a given market.
 * @param {string} marketId
 * @param {string} featureId — e.g. 'short_term_prediction'
 * @returns {boolean}
 */
export function hasFeature(marketId, featureId) {
  const features = MARKET_FEATURES[marketId] || MARKET_FEATURES.a_stock;
  return features.some((f) => f.id === featureId);
}

/**
 * Check whether a route path is allowed for a given market.
 * Settings (/settings) and stock-detail pages are always allowed.
 * @param {string} marketId
 * @param {string} pathname — e.g. '/data-processing/factor'
 * @returns {boolean}
 */
export function isRouteAllowed(marketId, pathname) {
  // Always-allowed paths
  if (pathname === '/' || pathname.startsWith('/settings') || pathname.startsWith('/stock-detail') || pathname.startsWith('/market/index') || pathname.startsWith('/api-docs') || pathname.startsWith('/overview')) {
    return true;
  }
  const features = MARKET_FEATURES[marketId] || MARKET_FEATURES.a_stock;
  return features.some((f) => f.path !== '/overview' && pathname.startsWith(f.path));
}

export { FEATURES, MARKET_FEATURES, CROSS_MARKET_FEATURES };
export default MARKET_FEATURES;
